// Turns the URL a visitor 404'd on into something they can act on. A dead link almost always
// still names what the person wanted ("/railing/cable-railing-posts/", "/old-black-post/p/12345/"),
// so the last meaningful path segment becomes a ready-made search term, and a product URL
// (…/p/<sku>) is called out as such because "discontinued" is a different story from "moved".

export interface NotFoundSuggestion {
  /** Words to search for, or null when the path carries nothing usable (e.g. "/", "/404/"). */
  term: string | null;
  /** True for product URLs (`/<slug>/p/<sku>/`), which have usually been retired, not moved. */
  isProduct: boolean;
}

const MAX_WORDS = 6;

// Segments that describe the site's structure rather than what the visitor was looking for.
const STRUCTURAL_SEGMENTS = new Set(['p', 'search', 'products', 'product', 'category', 'categories', 'index', '404', 'viewrail', 'shop']);

const toWords = (segment: string): string[] => {
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // A malformed escape would only yield gibberish; no suggestion beats a nonsense one.
    return [];
  }
  return decoded
    .replace(/\.(html?|php|aspx?)$/i, '')
    .replace(/[-_+.]+/g, ' ')
    .replace(/[^a-z0-9 ]/gi, ' ')
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word && !/^\d+$/.test(word));
};

export const suggestSearchFromPath = (pathname: string): NotFoundSuggestion => {
  const segments = pathname.split('/').filter(Boolean);
  const productIndex = segments.indexOf('p');
  const isProduct = productIndex > 0;
  // A product URL's slug sits before "/p/"; the SKU after it is noise to a shopper.
  const candidates = (isProduct ? segments.slice(0, productIndex) : segments).filter(
    (segment) => !STRUCTURAL_SEGMENTS.has(segment.toLowerCase()),
  );

  // The deepest segment is the most specific description; fall back up the path if it is empty
  // (e.g. a bare SKU or a numeric page).
  for (let i = candidates.length - 1; i >= 0; i -= 1) {
    const words = toWords(candidates[i]);
    if (words.length) return { term: words.slice(0, MAX_WORDS).join(' '), isProduct };
  }
  return { term: null, isProduct };
};
